// DataAccuracyFullSkeleton.tsx
import React from "react";
import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Grid,
} from "@mui/material";

type Props = {
  rows?: number;
};

const DataAccuracyFullSkeleton: React.FC<Props> = ({ rows = 8 }) => {
  return (
    <Box sx={{ p: 2 }}>
      {/* Top header card (title, pill, meta, description) */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={9}>
            {/* Title row */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
              <Skeleton variant="text" width="45%" height={36} />
              <Skeleton
                variant="rounded"
                width={120}
                height={32}
                sx={{ borderRadius: "16px" }}
              />
            </Box>

            {/* meta row: category / owners / created / updated */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
              <Skeleton variant="text" width={140} height={20} />
              {/* owners avatars */}
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Skeleton variant="circular" width={28} height={28} />
                <Skeleton variant="circular" width={28} height={28} />
                <Skeleton variant="circular" width={28} height={28} />
              </Box>

              <Skeleton variant="text" width={160} height={20} />
            </Box>

            {/* description paragraph */}
            <Box sx={{ mt: 1 }}>
              <Skeleton variant="rectangular" width="100%" height={56} />
            </Box>
          </Grid>

          {/* right side (small stats / last updated) */}
          <Grid
            item
            xs={3}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
              }}
            >
              <Skeleton variant="text" width={120} height={20} />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Subheader above table: section title + actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box>
          <Skeleton variant="text" width={220} height={28} />
          <Skeleton variant="text" width={180} height={20} />
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Skeleton variant="circular" width={36} height={36} />
          <Skeleton variant="circular" width={36} height={36} />
          <Skeleton variant="circular" width={36} height={36} />
          <Skeleton variant="circular" width={28} height={28} />
        </Box>
      </Box>

      {/* Table skeleton (no headers, just rows) */}
      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <TableRow key={rowIndex}>
                {/* Checkbox column */}
                <TableCell sx={{ width: 56 }}>
                  <Skeleton variant="circular" width={20} height={20} />
                </TableCell>

                {/* Asset */}
                <TableCell>
                  <Skeleton variant="text" width={140} height={20} />
                </TableCell>

                {/* Brand Name */}
                <TableCell>
                  <Skeleton variant="text" width={120} height={20} />
                </TableCell>

                {/* Brand ID */}
                <TableCell>
                  <Skeleton variant="text" width={60} height={20} />
                </TableCell>

                {/* Created */}
                <TableCell>
                  <Skeleton variant="text" width={90} height={20} />
                </TableCell>

                {/* Updated */}
                <TableCell>
                  <Skeleton variant="text" width={90} height={20} />
                </TableCell>

                {/* Assignee */}
                <TableCell>
                  <Skeleton variant="text" width={120} height={20} />
                </TableCell>

                {/* Status pill */}
                <TableCell>
                  <Skeleton
                    variant="rounded"
                    width={88}
                    height={28}
                    sx={{ borderRadius: "16px" }}
                  />
                </TableCell>

                {/* Spend */}
                <TableCell>
                  <Skeleton variant="text" width={60} height={20} />
                </TableCell>

                {/* CAC */}
                <TableCell>
                  <Skeleton variant="text" width={60} height={20} />
                </TableCell>

                {/* FTD */}
                <TableCell>
                  <Skeleton variant="text" width={60} height={20} />
                </TableCell>

                {/* QFTD */}
                <TableCell>
                  <Skeleton variant="text" width={60} height={20} />
                </TableCell>

                {/* Info icon */}
                <TableCell sx={{ width: 56 }}>
                  <Skeleton variant="circular" width={24} height={24} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DataAccuracyFullSkeleton;
